import moment from 'moment-timezone'
import selectn from 'selectn'
import typeOf from './typeOf'

// date time formatting related settings
const dateTime = {
  'dateFormats': {
    'time1': 'h:mm A',
    'date1': 'D MMM, YYYY',
    'date2': 'D/M/YYYY',
    'date3': 'DD/MM/YYYY',
    'date4': 'DDMMYYYY',
    'date5': 'Do MMMM YYYY',
    'date6': 'dddd, Do MMMM YYYY',
    'date7': 'ddd, D MMM YYYY',
    'date8': 'D/M',
    'date9': 'ddd',
    'date10': 'dddd, MMMM Do',
    'date11': 'MMMM YYYY',
    'date12': 'YYYYMMDD',
    'date13': 'MMMM Do, YYYY',
    'date14': 'dddd, MMMM D',
    'date15': 'D',
    'date16': 'ddd, MMM D',
    'date17': 'MMM Do, YYYY',
    'date18': 'Do MMM, hh.mmA z',
    'date19': 'YYYYMM',
    'timezone1': 'z',
    'timezone2': '(z)',
    'dateTime1': 'h:mm A, MMMM D, YYYY'
  },
  'dayNamesLong': 'Sunday Monday Tuesday Wednesday Thursday Friday Saturday'.split(' '),
  'dayNamesMedium': 'Sun Mon Tue Wed Thu Fri Sat'.split(' '),
  'dayNamesShort': 'Su Mo Tu We Th Fr Sa'.split(' '),
  'dayNamesXShort': 'S M T W T F S'.split(''),
  'monthNamesLong': 'January February March April May June July August September October November December'.split(' '),
  'monthNamesShort': 'Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec'.split(' ')
}

const DEFAULT_TIMEZONE = 'America/New_York'

export default {

  getTimezone: function (domainTimezone) {
    let timezone = DEFAULT_TIMEZONE
    if (process.browser) {
      timezone = moment.tz.guess() || timezone
    } else {
      timezone = domainTimezone || moment.tz.guess() || timezone
    }
    return timezone
  },

  getLocalTimezone: function () {
    return moment.tz.guess() || DEFAULT_TIMEZONE
  },

  dateToKey: function (oDate, timezone = '') {
    return this.dateFormat(oDate, 'date12', timezone)
  },

  paramToDate: function (sParam) {

    if (typeof sParam === 'undefined' || sParam === null || !sParam) {
      return null
    }
    var iYear = sParam.substr(0, 4)
    var iMonth = sParam.substr(4, 2)
    var iDay = sParam.substr(6, 2)

    return this.dateToObject(iYear + '-' + iMonth + '-' + iDay)

  },

  dateDayNamesMedium: function () {
    return dateTime.dayNamesMedium
  },
  dateDayNamesXShort: function () {
    return dateTime.dayNamesXShort
  },

  monthNamesShort: function () {
    return dateTime.monthNamesShort
  },
  monthNamesLong : function () {
    return dateTime.monthNamesLong
  },
  dateFormat: function (ISODate = '', dateFormat = '', timezone = '') {
    const formatsMap = dateTime.dateFormats
    const dateType = typeOf(ISODate)
    let dateString = ISODate

    if (ISODate && (dateType === 'string' || dateType === 'number')) {

      if (!dateFormat || typeOf(dateFormat) !== 'string') {
        dateFormat = 'MMMM Do, YYYY'
      } else {
        if (formatsMap[dateFormat]) {
          dateFormat = formatsMap[dateFormat]
        }
      }

      if (timezone && typeOf(timezone) !== 'string') {
        timezone = ''
      }

      // Convert to moment object
      const dateObj = moment(ISODate)

      // Format the date only if valid
      if (dateObj.isValid() === true) {
        if (timezone && moment.tz.zone(timezone)) {
          dateString = dateObj.tz(timezone).format(dateFormat)
        } else {
          dateString = dateObj.format(dateFormat)
        }
      }

    }

    return dateString

  },

  /**
   * Converts the date in d to a date-object using moment-timezone
   * @param  {String/object} d date in string/Object
   * @return {Object}          date-object
   */
  dateToObject: function (d) {
    return moment(d).toDate()
  },

  /**
   * Compare two dates (could be of any type supported by the convert
   * function above) and returns:
   *  -1 : if a < b
   *   0 : if a = b
   *   1 : if a > b
   * NaN : if a or b is an illegal date
   * NOTE: The code inside isFinite does an assignment (=).
   *
   * @param  {String/object} a date in string/Object
   * @param  {String/object} b date in string/Object
   * @return {Number}        as described above
   **/
  dateCompare: function (a, b) {
    return (
      isFinite(a = this.dateToObject(a).valueOf()) &&
        isFinite(b = this.dateToObject(b).valueOf()) ?
        (a > b) - (a < b) :
        NaN
    )
  },

  /**
   * Checks if date in d is between dates in start and end.
   * Returns a boolean or NaN:
   *    true  : if d is between start and end (inclusive)
   *    false : if d is before start or after end
   *    NaN   : if one or more of the dates is illegal.
   * NOTE: The code inside isFinite does an assignment (=).
   *
   * @param {String/object} d date in string/Object
   * @param {String/Object} start startdate
   * @param {String/Object} end startdate
   * @return {Boolean/NaN}  As described above
   **/
  dateInRange: function (d, start, end) {
    return (
      isFinite(d = this.dateToObject(d).valueOf()) &&
        isFinite(start = this.dateToObject(start).valueOf()) &&
        isFinite(end = this.dateToObject(end).valueOf()) ?
        start <= d && d <= end :
        NaN
    )
  },

  /**
   * Convert date/time as per the passed options of timezone and formatting
   * @param  {String} d date  String in ISO 8601 format
   * @param  {Object} options date formatting options
   * @return {String}         formatted time as per the options
   */
  convertDateTime: function (d, options) {
    // default return type
    let displayTime = ''
    // return default if the time is default
    let isDefaultTime = d.indexOf('T00:00Z') > -1 ? true : false
    if(isDefaultTime) {
      return displayTime
    }

    /**
     * default formatting options
     * UTC as time zone - 'America/New_York'
     * 24HR time format - HH:mm z
     */
    let timeZone = this.getLocalTimezone()
    let timeFormat = 'HH:mm z'
    // Override default options if options input has valid properties
    if (typeof selectn('timeZone', options) !== 'undefined') {
      timeZone = options.timeZone
    }

    if (typeof selectn('timeFormat', options) !== 'undefined') {
      timeFormat = options.timeFormat
    }
    // Convert to moment object
    let startTime = moment(d)
    // Format the date only if valid
    if (startTime.isValid() === true) {
      displayTime = startTime.tz(timeZone).format(timeFormat)
    }
    return displayTime
  },
  addMonths: function (date, months) {
    return moment(date).add(months, 'months').toDate()
  },
  subtractMonths: function (date, months) {
    return moment(date).subtract(months, 'months').toDate()
  },
  dateDiff: function (fromDate, toDate, diffType) {
    return moment(toDate).diff(moment(fromDate), diffType)
  }
}

/* Date comparison taken from
 * http://stackoverflow.com/questions/492994/compare-two-dates-with-javascript
 */




// WEBPACK FOOTER //
// ../app/lib/utils/dateformat.js